import { Injectable } from "@angular/core";
import { GetUserInstructionDetails, LlmInstructionFormat, RegimenInstructionFormat } from "@cureskin/api-client/src/server-api";
import { ApiClientConstant, Table } from "@cureskin/api-client";
import { UserService } from "@services/user/user.service";
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppConfig } from "../../app/app.config";
import { AllocatedRegimen } from "../../app/app-constant-types";

/** Tag used on Catalog.extraImagesWithTags for the image shown in the regimen-delivered banner. */
const REGIMEN_DELIVERED_BANNER_IMAGE_TAG = 'regimen-delivered-banner';

export interface ProductIngredients {
  keyActive?: string;
  supportingActive?: string;
  baseIngredients?: string;
}

export interface InstructionVideo {
  en?: string;
  hi?: string;
  [key: string]: string | undefined;
}

export interface BannerInstructionVideo {
  url: string;
  poster?: string;
  durationLabel?: string;
  /** True when `poster` came from the product's `extraImagesWithTags` (regimen-delivered-banner tag). */
  posterFromTag?: boolean;
}

/** Entry shape of Catalog.extraImagesWithTags (see api Catalog schema `CatalogExtraImageWithTag`). */
export interface CatalogTaggedImage {
  imageLink?: string;
  tag?: string;
  priority?: number;
}

export interface ProductData {
  title: string;
  productImage?: string;
  description?: string;
  ingredients?: ProductIngredients;
  purpose: string;
}

export interface InstructionSetData {
  quantityImage?: string[];
  frequencyOfApplication?: string;
  durationOfApplication?: string;
  quantity?: string;
  areaOfApplication?: string;
  notes?: string[];
  warnings?: string[];
  paidInstructionVideo?: InstructionVideo;
  unpaidInstructionVideo?: InstructionVideo;
  newFrequencyOfApplication?: DayName[];
  displayDays?: string;
}

export interface FormattedInstructionData {
  product: ProductData;
  instructionSet: InstructionSetData;
  productName: string;
  productDescription?: string;
}

export interface FrequencyData {
  frequencyOfApplicationEnglish?: string;
  frequencyOfApplication?: string;
  isCustomInstruction?: boolean;
}

export type DayCode = 'M' | 'T' | 'W' | 'Th' | 'F' | 'Sa' | 'Su';
export type DayName = 'Mon' | 'Tue' | 'Wed' | 'Thr' | 'Fri' | 'Sat' | 'Sun';

export interface DayMapping {
  [key: string]: DayName;
}

@Injectable({ providedIn: 'root' })
export class InstructionsService {
  private cachedUserInstructions: GetUserInstructionDetails | null = null;
  private daysData: DayName[] = [];

  constructor(
    private readonly appConfig: AppConfig,
    private readonly userService: UserService,
    private readonly conn: ConnectionService,
    private readonly eventLoggerService: EventLoggerService,
  ) {}

  setUserInstructions(data: GetUserInstructionDetails): void {
    if (data.userInstructions.ADDITIONAL_PRODUCTS.length <=0 ) return;
    this.cachedUserInstructions = data;
  }

  getUserInstructions(): GetUserInstructionDetails | null {
    return this.cachedUserInstructions;
  }

  getAddOnInstructions(): GetUserInstructionDetails['userInstructions']['ADDITIONAL_PRODUCTS'] {
    return this.cachedUserInstructions?.userInstructions?.ADDITIONAL_PRODUCTS || [];
  }

  clearUserInstructions(): void {
    this.cachedUserInstructions = null;
  }

  generateCommonDataForInstruction(
    item: LlmInstructionFormat & RegimenInstructionFormat
  ): FormattedInstructionData {

    const formattedData: FormattedInstructionData = {
      product: {
        title: item.name,
        productImage: item.productImage,
        description: item.description,
        ingredients: item.ingredients,
        purpose: item.purpose
      },
      instructionSet: {
        quantityImage: item.quantityImage,
        frequencyOfApplication: item.frequencyOfApplicationEnglish,
        durationOfApplication: item.durationOfApplication,
        quantity: item.quantity,
        areaOfApplication: item.areaOfApplication,
        notes: item.notes,
        warnings: item.warnings,
        paidInstructionVideo: item.paidInstructionVideo,
        unpaidInstructionVideo: (item as { unpaidInstructionVideo?: InstructionVideo }).unpaidInstructionVideo,
      },
      productName: item.name,
      productDescription: item.description,
    };

    // Process frequency data for "not to be used" functionality
    const frequencyText = item.frequencyOfApplicationEnglish;
    if (frequencyText) {
      const frequencyResult = this.formatFrequency(item);
      if (Array.isArray(frequencyResult) && frequencyResult.length > 0) {
        formattedData.instructionSet.newFrequencyOfApplication = frequencyResult;
        formattedData.instructionSet.displayDays = this.formatStringDays(frequencyResult);
      }
    }

    return formattedData;
  }

  /**
   * This method is used to format the instructions frequency of usage to days array.
   */
  formatFrequency(data: FrequencyData | string, isOldUi: boolean = false): DayName[] | string {
    let text: string;
    if (isOldUi) {
      text = data as string;
    } else {
      const frequencyData = data as FrequencyData;
      text = frequencyData.frequencyOfApplicationEnglish || frequencyData.frequencyOfApplication || '';
    }
    if (!text) return [];

    // Trim whitespace and newlines
    text = text.trim();
    const daysArray = text.split('/').map((day: string) => day.trim());
    const dayObjList = Object.keys(this.appConfig.Shared.FrequencyOfUsage.Days);
    const hasAllElems = daysArray.every((elem: DayCode): boolean => dayObjList.includes(elem));

    if (isOldUi && !hasAllElems) {
      return text;
    }
    if (!isOldUi && !hasAllElems) {
      if (text.toLowerCase() !== 'all days') {
        // eslint-disable-next-line no-param-reassign
        (data as FrequencyData).isCustomInstruction = true;
      }
      return [];
    }
    this.daysData = [];
    daysArray.forEach((element: DayCode): void => {
      const dayName = this.appConfig.Shared.FrequencyOfUsage.Days[element] as DayName;
      if (dayName) {
        this.daysData.push(dayName);
      }
    });
    return this.daysData;
  }

  async saveReminderToggle(enabled: boolean): Promise<boolean> {
    const currentUser = this.conn.getActingUser();
    if (!currentUser) return !enabled;
    try {
      currentUser.set('hasEnabledReminderForInstructions', enabled);
      await currentUser.save();
      this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
        name: 'instruction-reminder',
        userId: currentUser.id,
        enabled,
      }));
      this.eventLoggerService.setUserProperty({ is_instruction_reminder_enabled: enabled });
      return enabled;
    } catch {
      return !enabled;
    }
  }

  /**
   * This is used to format the array to a string with commas and & operator at last
   */
  formatStringDays(list: DayName[]): string {
    if (!list?.length) {
      return '';
    }
    if (list.length === 1) {
      return list[0];
    }
    if (list.length === 2) {
      return `${list[0]} & ${list[1]}`;
    }
    return `${list.slice(0, -1).join(', ')} & ${list.slice(-1)[0]}`;
  }

  getInstructionVideoUrl(instructionSet: InstructionSetData, user: Table.User): string {
    const videoLang = this.getVideoLanguage(user);
    const videoMap = this.userService.isPaidUser(user)
      ? instructionSet?.paidInstructionVideo
      : (instructionSet?.unpaidInstructionVideo || instructionSet?.paidInstructionVideo);
    return videoMap?.[videoLang] || videoMap?.en || '';
  }

  extractRegimenInstructionVideos(
    regimen: AllocatedRegimen,
    user: Table.User,
    maxVideos: number,
  ): { videos: BannerInstructionVideo[]; totalProducts: number } {
    const items: any[] = [...(regimen?.morning || []), ...(regimen?.night || [])];
    const seenProductIds = new Set<string>();
    const videos: BannerInstructionVideo[] = [];
    items.forEach((item: any): void => {
      const product: any = item.product || {};
      const productId = product.objectId || product.id;
      if (productId) {
        if (seenProductIds.has(productId)) return;
        seenProductIds.add(productId);
      }
      const url = this.getInstructionVideoUrl(item.instructionSet, user);
      if (!url || videos.some((video: BannerInstructionVideo): boolean => video.url === url)) return;
      const taggedPoster = this.getRegimenDeliveredBannerImage(product);
      const poster = taggedPoster
        || product.rebrandedImageWithoutBackground?.[0]
        || product.productUnboxedImage?.[0]
        || product.productImage
        || (product.rebrandedImageWithBackground?.length && product.rebrandedImageWithBackground[0]);
      videos.push({ url, poster: poster || undefined, posterFromTag: !!taggedPoster });
    });
    return { videos: videos.slice(0, maxVideos), totalProducts: seenProductIds.size };
  }

  /**
   * Highest-priority poster source: the `regimen-delivered-banner` tagged image from the
   * product's `extraImagesWithTags`. Returns the lowest-priority-number entry's `imageLink`,
   * or undefined when no such tagged image exists (callers fall back to the legacy image fields).
   */
  private getRegimenDeliveredBannerImage(product: { extraImagesWithTags?: CatalogTaggedImage[] }): string | undefined {
    return (product?.extraImagesWithTags ?? [])
      .filter((image: CatalogTaggedImage): boolean => image.tag === REGIMEN_DELIVERED_BANNER_IMAGE_TAG && !!image.imageLink)
      .sort((a: CatalogTaggedImage, b: CatalogTaggedImage): number => (a.priority ?? 0) - (b.priority ?? 0))[0]?.imageLink;
  }

  private getVideoLanguage(user: Table.User): string {
    const commLang = user?.get('communicationLanguagePreference');
    const appLang = user?.get('languagePreference');
    return commLang || appLang || ApiClientConstant.LanguageString.EN;
  }
}
